import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Plus, Pencil, Trash2, Copy, List, Star, StarOff, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name_en: '',
  name_ar: '',
  description_en: '',
  description_ar: '',
  is_active: true,
  is_default: false
};

export default function PriceListsPage() {
  const { t, lang, isRTL } = useLanguage();
  const [priceLists, setPriceLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicating, setDuplicating] = useState(null);
  const [duplicateForm, setDuplicateForm] = useState({ name_en: '', name_ar: '' });

  const getName = (item) => lang === 'ar' ? item.name_ar : item.name_en;
  const getDesc = (item) => lang === 'ar' ? item.description_ar : item.description_en;

  const fetchPriceLists = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/price-lists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPriceLists(res.data);
    } catch (err) {
      toast.error('Failed to load price lists');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPriceLists();
  }, [fetchPriceLists]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (pl) => {
    setEditing(pl);
    setForm({
      name_en: pl.name_en,
      name_ar: pl.name_ar,
      description_en: pl.description_en || '',
      description_ar: pl.description_ar || '',
      is_active: pl.is_active,
      is_default: pl.is_default
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name_en || !form.name_ar) {
      toast.error('Please fill required fields');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (editing) {
        await axios.put(`${API}/price-lists/${editing.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Price list updated');
      } else {
        await axios.post(`${API}/price-lists`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Price list created');
      }
      setDialogOpen(false);
      fetchPriceLists();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pl) => {
    if (pl.is_default) {
      toast.error('Cannot delete default price list');
      return;
    }
    if (!window.confirm(`Delete "${getName(pl)}"? This will delete all categories and products.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/price-lists/${pl.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Price list deleted');
      fetchPriceLists();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const openDuplicate = (pl) => {
    setDuplicating(pl);
    setDuplicateForm({
      name_en: `${pl.name_en} (Copy)`,
      name_ar: `${pl.name_ar} (نسخة)`
    });
    setDuplicateDialogOpen(true);
  };

  const handleDuplicate = async () => {
    if (!duplicating) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/price-lists/${duplicating.id}/duplicate`,
        { name_en: duplicateForm.name_en, name_ar: duplicateForm.name_ar },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Price list duplicated');
      setDuplicateDialogOpen(false);
      fetchPriceLists();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to duplicate');
    } finally {
      setSaving(false);
    }
  };

  const setAsDefault = async (pl) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/price-lists/${pl.id}`, { is_default: true }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Default price list updated');
      fetchPriceLists();
    } catch (err) {
      toast.error('Failed to set default');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0056A6] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{t('priceLists')}</h1>
          <p className="text-sm text-slate-500 mt-1">{t('managePriceLists')}</p>
        </div>
        <Button onClick={openNew} className="bg-[#0056A6] hover:bg-[#004080]" data-testid="add-price-list-btn">
          <Plus className="h-4 w-4 me-2" />
          {t('addPriceList')}
        </Button>
      </div>

      {/* Price Lists Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {priceLists.map((pl) => (
          <div
            key={pl.id}
            className={`bg-white rounded-xl border ${pl.is_default ? 'border-[#0056A6] ring-2 ring-[#0056A6]/20' : 'border-slate-200'} p-5 hover:shadow-md transition-shadow`}
            data-testid={`price-list-card-${pl.id}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={`w-10 h-10 rounded-lg ${pl.is_default ? 'bg-[#0056A6]' : 'bg-slate-100'} flex items-center justify-center`}>
                  <List className={`h-5 w-5 ${pl.is_default ? 'text-white' : 'text-slate-500'}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">{getName(pl)}</h3>
                  {pl.is_default && (
                    <Badge variant="secondary" className="text-[0.65rem] bg-[#0056A6]/10 text-[#0056A6]">
                      {t('default')}
                    </Badge>
                  )}
                </div>
              </div>
              <Badge variant={pl.is_active ? 'default' : 'secondary'} className={pl.is_active ? 'bg-emerald-100 text-emerald-700' : ''}>
                {pl.is_active ? t('active') : t('inactive')}
              </Badge>
            </div>

            {getDesc(pl) && (
              <p className="text-sm text-slate-500 mb-3 line-clamp-2">{getDesc(pl)}</p>
            )}

            <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
              <span className="flex items-center gap-1">
                <BarChart3 className="h-4 w-4" />
                {pl.category_count || 0} {t('categories')}
              </span>
              <span>{pl.product_count || 0} {t('products')}</span>
            </div>

            <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
              <Button variant="ghost" size="sm" onClick={() => openEdit(pl)} data-testid={`edit-pl-${pl.id}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => openDuplicate(pl)} data-testid={`duplicate-pl-${pl.id}`}>
                <Copy className="h-4 w-4" />
              </Button>
              {!pl.is_default && (
                <>
                  <Button variant="ghost" size="sm" onClick={() => setAsDefault(pl)} data-testid={`default-pl-${pl.id}`}>
                    <Star className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(pl)} data-testid={`delete-pl-${pl.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {priceLists.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
          <List className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">{t('noPriceLists')}</p>
          <Button onClick={openNew} variant="outline" className="mt-4">
            <Plus className="h-4 w-4 me-2" />
            {t('createFirst')}
          </Button>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? t('editPriceList') : t('addPriceList')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('nameEn')} *</Label>
              <Input
                value={form.name_en}
                onChange={e => setForm(p => ({ ...p, name_en: e.target.value }))}
                className="mt-1.5"
                data-testid="pl-name-en"
              />
            </div>
            <div>
              <Label>{t('nameAr')} *</Label>
              <Input
                value={form.name_ar}
                onChange={e => setForm(p => ({ ...p, name_ar: e.target.value }))}
                className="mt-1.5"
                dir="rtl"
                data-testid="pl-name-ar"
              />
            </div>
            <div>
              <Label>{t('descriptionEn')}</Label>
              <Input
                value={form.description_en}
                onChange={e => setForm(p => ({ ...p, description_en: e.target.value }))}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>{t('descriptionAr')}</Label>
              <Input
                value={form.description_ar}
                onChange={e => setForm(p => ({ ...p, description_ar: e.target.value }))}
                className="mt-1.5"
                dir="rtl"
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Switch
                  checked={form.is_default}
                  onCheckedChange={v => setForm(p => ({ ...p, is_default: v }))}
                />
                <Label>{t('default')}</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={form.is_active}
                  onCheckedChange={v => setForm(p => ({ ...p, is_active: v }))}
                />
                <Label>{t('active')}</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('cancel')}</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0056A6] hover:bg-[#004080]">
              {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Dialog */}
      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('duplicatePriceList')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              {t('duplicateDescription')}
            </p>
            <div>
              <Label>{t('nameEn')} *</Label>
              <Input
                value={duplicateForm.name_en}
                onChange={e => setDuplicateForm(p => ({ ...p, name_en: e.target.value }))}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>{t('nameAr')} *</Label>
              <Input
                value={duplicateForm.name_ar}
                onChange={e => setDuplicateForm(p => ({ ...p, name_ar: e.target.value }))}
                className="mt-1.5"
                dir="rtl"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateDialogOpen(false)}>{t('cancel')}</Button>
            <Button onClick={handleDuplicate} disabled={saving} className="bg-[#0056A6] hover:bg-[#004080]">
              {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : t('duplicate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
